<template>
  <div>
    <Header></Header>
    <div class="col-12 d-inline-flex">
      <div class="col-10 no-overflow ms-3 me-3 pt-3" style="position: relative;">
        <div class="stripe-configuration">
          <h2>{{ editMode ? 'Edit' : 'Create' }} Application</h2>
          <form @submit.prevent="saveChanges">
            <div v-for="field in fields" :key="field.name" class="form-group">
              <label :for="field.name">{{ field.label }}:</label>
              <input v-if="field.name !== 'active' && field.name !== 'sport'"
                     :id="field.name" v-model="editedConfig[field.name]" :style="{ minWidth: '500px', width: editedConfig[field.name] ? `${editedConfig[field.name].length * 10}px` : '100px' }"
                     type="text" class="form-control responsive-input" :disabled="(field.name === 'application' || field.name === 'appName') && editMode">
              <select v-else-if="field.name === 'sport'" :id="field.name" v-model="editedConfig[field.name]" :style="{ minWidth: '500px', width: editedConfig[field.name] ? `${editedConfig[field.name].length * 10}px` : '100px' }" class="form-select responsive-input">
                <option :value="undefined">—</option>
                <option v-for="s in sportOptions" :key="s" :value="s">{{ s }}</option>
              </select>
              <input class="checkbox" v-else
                     type="checkbox" :id="field.name" v-model="editedConfig[field.name]">
            </div>

            <div class="email-templates-section">
              <h3>Email Templates</h3>

              <h4>Standard Emails</h4>
              <p class="section-hint">
                Flat, one-off emails looked up by their exact key name (welcome email, password reset, receipts, etc).
                The scheduler never touches these. Key names for known types must match exactly — set the Brevo template ID
                for the ones you use, or add a custom key if this application needs one that isn't listed.
              </p>
              <table class="table table-bordered table-striped email-templates-table">
                <thead>
                <tr>
                  <th>Key</th>
                  <th>Brevo Template ID</th>
                  <th></th>
                </tr>
                </thead>
                <tbody>
                <tr v-for="row in standardEmails" :key="row._id">
                  <td>
                    <input type="text" class="form-control" v-model="row.key">
                  </td>
                  <td>
                    <input type="number" class="form-control" v-model.number="row.templateId">
                  </td>
                  <td>
                    <a role="button" class="procricket-btn-stop" @click="removeStandardEmailRow(row)">Remove</a>
                  </td>
                </tr>
                </tbody>
              </table>
              <a role="button" class="procricket-btn--reverse" @click="addStandardEmailRow">+ Add custom standard email</a>

              <h4 class="mt-4">Scheduler Reminders</h4>
              <p class="section-hint">
                Plan-expiry reminders driven by the daily scheduler. The label is only for your own reference — the code
                never reads it, only the fields below matter. Template ID and Enabled are required; a row missing a
                Template ID is silently ignored by the scheduler.
              </p>
              <table class="table table-bordered table-striped email-templates-table">
                <thead>
                <tr>
                  <th class="reminder-label-col">Label</th>
                  <th class="reminder-narrow-col">Template ID</th>
                  <th class="reminder-narrow-col">Days offset</th>
                  <th>Timing</th>
                  <th>Audience</th>
                  <th>Respect unsubscribe</th>
                  <th>Respect clean status</th>
                  <th>Enabled</th>
                  <th></th>
                </tr>
                </thead>
                <tbody>
                <tr v-for="row in reminders" :key="row._id" :class="{ 'duplicate-offset-row': isDuplicateOffset(row) }">
                  <td class="reminder-label-col">
                    <input type="text" class="form-control" v-model="row.key" placeholder="e.g. reminder-plan-expires-in-3-days">
                  </td>
                  <td class="reminder-narrow-col">
                    <input type="number" class="form-control" v-model.number="row.templateId" min="0">
                  </td>
                  <td class="reminder-narrow-col">
                    <input type="number" class="form-control" v-model.number="row.days" min="0">
                  </td>
                  <td>
                    <select class="form-control" v-model="row.before">
                      <option :value="true">Before expiry</option>
                      <option :value="false">After expiry (lapsed)</option>
                    </select>
                  </td>
                  <td>
                    <label class="d-block"><input type="checkbox" v-model="row.subscribed"> Paid</label>
                    <label class="d-block"><input type="checkbox" v-model="row.trial"> Trial</label>
                  </td>
                  <td class="text-center"><input type="checkbox" v-model="row.emailSubscribe"></td>
                  <td class="text-center"><input type="checkbox" v-model="row.emailCleanStatus"></td>
                  <td class="text-center"><input type="checkbox" v-model="row.status"></td>
                  <td>
                    <a role="button" class="procricket-btn-stop" @click="removeReminderRow(row)">Remove</a>
                  </td>
                </tr>
                </tbody>
              </table>
              <a role="button" class="procricket-btn--reverse" @click="addReminderRow">+ Add reminder</a>

              <div v-if="isDuplicateOffsetPresent" class="alert-warning mt-2">
                Some reminders share the same days/timing offset — they're grouped together above and will all fire independently, this isn't a conflict.
              </div>
            </div>

            <div v-if="error != ''" class="error-message">{{error}}</div>
            <br>
            <a role="button" class="procricket-btn" @click="saveChanges">{{ editMode ? 'Save Changes' : 'Create Application' }}</a>
          </form>
        </div>
      </div>

    </div>
  </div>
</template>

<script>
import { defineComponent } from 'vue'
import Header from '@/components/Header.vue'
import {Application} from '@/model/models'
import AdminService from "@/services/admin-service";

export default defineComponent({
  name: 'ApplicationEdit',
  components: {
    Header,
  },
  data () {
    return {
      editedConfig: {
        application : this.$route.params.application,
        domains : this.$route.params.domains,
        description: this.$route.params.description,
        hlsPrefix : this.$route.params.hlsPrefix,
        hlsSufix : this.$route.params.hlsSufix,
        edgePort: this.$route.params.edgePort,
        appName : this.$route.params.appName,
        sport: this.$route.params.sport,
        vidictLicense : this.$route.params.vidictLicense,
        vidictSecret: this.$route.params.vidictSecret,
        emailTemplates : '',
        active: this.$route.params.active,
      },
      editMode: false,
      fields: [
        { name: "application", label: "Application" },
        { name: "domains", label: "Domains" },
        { name: "description", label: "Description" },
        { name: "hlsPrefix", label: "HLS prefix" },
        { name: "hlsSufix", label: "HLS sufix" },
        { name: "edgePort", label: "Edge port" },
        { name: "appName", label: "App name" },
        { name: "sport", label: "Sport" },
        { name: "vidictLicense", label: "Vidict license" },
        { name: "vidictSecret", label: "Vidict secret" },
        { name: "active", label: "Active" }
      ],
      // Empty until created()'s AdminService.getCalendarStaticData() call resolves - the same
      // Sport enum list the calendar screens use (cached in sessionStorage under
      // 'calendarStatic' by that same call), not a separate hardcoded copy.
      sportOptions: [],
      standardEmails: [],
      reminders: [],
      nextRowId: 1,
      error: ""
    }
  },
  computed: {
    duplicateOffsetCounts() {
      const counts = {};
      this.reminders.forEach((r) => {
        const k = `${r.days}|${r.before}`;
        counts[k] = (counts[k] || 0) + 1;
      });
      return counts;
    },
    isDuplicateOffsetPresent() {
      return Object.values(this.duplicateOffsetCounts).some((c) => c > 1);
    }
  },
  created() {
    if (this.$route.params.application) {
      this.editMode = true;
    }
    // emailTemplates is handed off via sessionStorage rather than the URL, since it
    // can be a large JSON blob and URLs have practical length limits. Applications.vue
    // sets this right before navigating here.
    const storedEmailTemplates = sessionStorage.getItem('applicationEditEmailTemplates') || '';
    sessionStorage.removeItem('applicationEditEmailTemplates');
    this.editedConfig.emailTemplates = storedEmailTemplates;
    this.parseEmailTemplates(storedEmailTemplates);

    // Same sports list, same request as the calendar screens: getCalendarStaticData() serves
    // the sessionStorage copy ('calendarStatic') when present, otherwise fetches and caches it.
    AdminService.getCalendarStaticData().then((data) => {
      this.sportOptions = data.sports;
    }).catch((error) => {
      console.error("Error loading calendar static data:", error);
    });
  },
  methods: {
    makeRowId() {
      return this.nextRowId++;
    },
    parseEmailTemplates(raw) {
      this.standardEmails = [];
      this.reminders = [];

      let obj = {};
      if (raw) {
        try {
          obj = typeof raw === 'string' ? JSON.parse(raw) : raw;
        } catch (e) {
          obj = {};
        }
      }

      // Each entry's shape tells us what it is: a bare number is a standard/flat
      // email (looked up by exact key), an object is a scheduler-driven reminder.
      // No fixed key list — whatever is in the JSON is what gets shown.
      Object.entries(obj || {}).forEach(([key, value]) => {
        if (value !== null && typeof value === 'object') {
          this.reminders.push({
            _id: this.makeRowId(),
            key,
            templateId: value.templateId ?? null,
            days: value.days ?? 0,
            before: value.before !== undefined ? !!value.before : true,
            subscribed: value.subscribed !== undefined ? !!value.subscribed : true,
            trial: value.trial !== undefined ? !!value.trial : false,
            emailSubscribe: value.emailSubscribe !== undefined ? !!value.emailSubscribe : true,
            emailCleanStatus: value.emailCleanStatus !== undefined ? !!value.emailCleanStatus : false,
            status: value.status !== undefined ? !!value.status : false
          });
        } else {
          this.standardEmails.push({
            _id: this.makeRowId(),
            key,
            templateId: value === null || value === undefined || value === '' ? null : Number(value)
          });
        }
      });

      this.standardEmails.sort((a, b) => a.key.localeCompare(b.key));

      // Group entries sharing the same (days, before) offset together. This only
      // runs once on load — rows added afterward are appended and stay put, so
      // editing a row's days/timing doesn't reshuffle the table underneath you.
      this.reminders.sort((a, b) => {
        if (a.before !== b.before) return a.before ? -1 : 1;
        return (Number(a.days) || 0) - (Number(b.days) || 0);
      });
    },
    addStandardEmailRow() {
      this.standardEmails.push({ _id: this.makeRowId(), key: '', templateId: null });
    },
    removeStandardEmailRow(row) {
      this.standardEmails = this.standardEmails.filter((r) => r._id !== row._id);
    },
    addReminderRow() {
      this.reminders.push({
        _id: this.makeRowId(),
        key: '',
        templateId: null,
        days: 0,
        before: true,
        subscribed: true,
        trial: false,
        emailSubscribe: true,
        emailCleanStatus: false,
        status: true
      });
    },
    removeReminderRow(row) {
      this.reminders = this.reminders.filter((r) => r._id !== row._id);
    },
    isDuplicateOffset(row) {
      return this.duplicateOffsetCounts[`${row.days}|${row.before}`] > 1;
    },
    buildEmailTemplates() {
      const result = {};
      const errors = [];
      const seenKeys = new Set();

      this.standardEmails.forEach((row) => {
        const key = (row.key || '').trim();
        const hasTemplateId = !(row.templateId === null || row.templateId === undefined || row.templateId === '');
        if (!key && !hasTemplateId) {
          return;
        }
        if (!key) {
          errors.push('A standard email row has a Template ID but no key name.');
          return;
        }
        if (!hasTemplateId) {
          return;
        }
        if (seenKeys.has(key)) {
          errors.push(`Duplicate email template key "${key}".`);
          return;
        }
        seenKeys.add(key);
        result[key] = Number(row.templateId);
      });

      this.reminders.forEach((row, idx) => {
        const key = (row.key || '').trim() || `reminder-${idx + 1}`;
        const hasTemplateId = !(row.templateId === null || row.templateId === undefined || row.templateId === '');
        if (!hasTemplateId) {
          errors.push(`Reminder "${key}" is missing a required Template ID.`);
          return;
        }
        if (seenKeys.has(key)) {
          errors.push(`Duplicate email template key "${key}".`);
          return;
        }
        seenKeys.add(key);

        // templateId and status are the only required fields (per the backend contract) -
        // everything else has a backend-side default, so we only write it out when it
        // differs from that default. Keeps the saved JSON minimal and avoids the tool
        // "locking in" a value the backend would otherwise infer.
        const entry = {
          templateId: Number(row.templateId),
          status: !!row.status
        };
        const days = Number(row.days) || 0;
        if (days !== 0) entry.days = days;
        if (!row.before) entry.before = false;
        if (!row.subscribed) entry.subscribed = false;
        if (row.trial) entry.trial = true;
        if (!row.emailSubscribe) entry.emailSubscribe = false;
        if (row.emailCleanStatus) entry.emailCleanStatus = true;

        result[key] = entry;
      });

      return { json: JSON.stringify(result), errors };
    },
    saveChanges() {
      this.error = "";
      const { json, errors } = this.buildEmailTemplates();
      if (errors.length) {
        this.error = errors.join(' ');
        return;
      }
      this.editedConfig.emailTemplates = json;

      const dataToSave = new Application(
          this.editedConfig.application,
          this.editedConfig.domains,
          this.editedConfig.description,
          this.editedConfig.hlsPrefix,
          this.editedConfig.hlsSufix,
          this.editedConfig.edgePort,
          this.editedConfig.appName,
          this.editedConfig.sport,
          this.editedConfig.vidictLicense,
          this.editedConfig.vidictSecret,
          this.editedConfig.emailTemplates,
          this.editedConfig.active
      );
      if (this.editMode) {
        // Editing an existing configuration
        AdminService.updateApplication(dataToSave).then(() => {
          alert('Update complete');
          // Redirect to the page
          this.$router.push({ name: 'Applications' });
        }).catch((error) => {
          if (error.response && error.response.data && error.response.data.message) {
            alert(error.response.data.message);
          }
        });
      }
    }

  }
})
</script>
<style src="../../assets/style.css">
</style>
